import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useMemo } from 'react'

import CheckActionIcon from '@/assets/icons/icon-check-action.svg'
import InfoIcon from '@/assets/icons/icon-info.svg'
import PlusIcon from '@/assets/icons/icon-plus.svg'
import SelectionIcon from '@/assets/icons/icon-selection.svg'
import TrashIcon from '@/assets/icons/icon-trash.svg'
import { Button } from '@/components/ui/button.tsx'
import { SearchInput } from '@/components/ui/search-input.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import type { RestrictedWord } from '@/modules/restricted-words/types.ts'

interface Props {
  onSelectToggle: () => void
  isSelectionOpen: boolean
  selectedItems: RestrictedWord[]
  wordsState: string
  setWordsState: Dispatch<SetStateAction<string>>
  searchQuery: string
  setSearchQuery: Dispatch<SetStateAction<string>>
  onAddOpen: () => void
  onCheckClick: () => void
  isAllSelectedCommon: boolean
  isAllSelectedCustom: boolean
  isToggleSelectedLoading: boolean
  isDeleteSelectedLoading: boolean
  onToggleSelected: (state: boolean) => void
  onDeleteSelectedClick: () => void
}

export const RestrictedWordsTools = ({
  onSelectToggle,
  isSelectionOpen,
  selectedItems,
  wordsState,
  setWordsState,
  searchQuery,
  setSearchQuery,
  onAddOpen,
  isAllSelectedCommon,
  isAllSelectedCustom,
  onDeleteSelectedClick,
  onToggleSelected,
  onCheckClick,
  isToggleSelectedLoading,
  isDeleteSelectedLoading,
}: Props) => {
  const isAllSelectedDisabled = useMemo(
    () => !!selectedItems.length && selectedItems.every((s) => s.disabled),
    [selectedItems],
  )
  const handleToggleSelectedClick = useCallback(() => {
    onToggleSelected(isAllSelectedDisabled)
  }, [isAllSelectedDisabled, onToggleSelected])

  return (
    <div className="my-2 flex flex-col justify-between lg:flex-row">
      <div className="mb-1.5 flex items-center lg:mb-0">
        <Button
          onClick={onSelectToggle}
          variant={isSelectionOpen ? 'default' : 'outline'}
          className="flex items-center"
        >
          <SelectionIcon className="mr-1.5" />{' '}
          {isSelectionOpen ? 'Cancel Selection' : 'Select Words'}
        </Button>
        <Tooltip
          contentClassName="dark:bg-secondary-500"
          content={
            <div>
              <div className="mb-1 flex items-center text-xs font-medium">
                <div className="mr-1.5 h-2.5 w-2.5 rounded-full border bg-secondary-50 dark:border-none dark:bg-secondary-600" />
                Default
              </div>
              <div className="mb-1 flex items-center text-xs font-medium">
                <div className="mr-1.5 h-2.5 w-2.5 rounded-full border border-border-secondary bg-white dark:bg-secondary-700" />
                Deactivated
              </div>
              <div className="flex items-center text-xs font-medium">
                <div className="mr-1.5 h-2.5 w-2.5 rounded-full bg-[#E2F0FE] dark:bg-primary-900" />
                Custom
              </div>
            </div>
          }
        >
          <InfoIcon className="ml-2 h-6 w-6" />
        </Tooltip>
      </div>
      <div className="flex flex-col items-start justify-end lg:flex-row lg:flex-wrap lg:items-center">
        {isSelectionOpen && (
          <span className="mr-2 hidden text-sm lg:inline-block">
            Selected {selectedItems.length}
          </span>
        )}
        <div className="mb-1.5 mr-2 w-full lg:mb-0 lg:w-[200px]">
          <Select
            value={wordsState}
            onValueChange={setWordsState}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="default">Default Words</SelectItem>
                <SelectItem value="custom">Custom Words</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <SearchInput
          className="mb-1.5 mr-2 w-full py-1.5 lg:mb-0 lg:w-[200px]"
          value={searchQuery}
          placeholder="Search words"
          onInputChange={setSearchQuery}
        />
        {!isSelectionOpen && (
          <Button
            onClick={onCheckClick}
            size="sm"
            variant="ghost"
            className="mr-2 flex w-full items-center gap-1 lg:w-[unset]"
          >
            <CheckActionIcon
              width={25}
              height={25}
            />
            Check Message
          </Button>
        )}
        {!isSelectionOpen && (
          <Button
            onClick={onAddOpen}
            size="sm"
            className="flex w-full items-center gap-1 lg:w-[unset]"
          >
            <PlusIcon
              width={25}
              height={25}
            />
            Add Word
          </Button>
        )}
        {isAllSelectedCommon && (
          <Button
            isLoading={isToggleSelectedLoading}
            onClick={handleToggleSelectedClick}
            size="sm"
            className="ml-2"
            variant={isAllSelectedDisabled ? 'default' : 'destructive'}
          >
            {isAllSelectedDisabled ? 'Enable' : 'Disable'}
          </Button>
        )}
        {isAllSelectedCustom && (
          <Button
            size="sm"
            variant="destructive"
            className="ml-2 flex w-full items-center gap-1 lg:w-[unset]"
            onClick={onDeleteSelectedClick}
            isLoading={isDeleteSelectedLoading}
          >
            <TrashIcon className="ml-1" /> Delete
          </Button>
        )}
      </div>
    </div>
  )
}
